import {
  menusEditorSettingsPanelActionSrc159Evid166,
  menusEditorSettingsPanelTabSwitchSrc159Evid167,
  menusEditorSettingsPanelMenusTabSrc159Evid169,
  menusEditorSettingsPanelLayoutTabSrc159Evid168,
  menusEditorSettingsPanelDisplayTabSrc159Evid172,
  // TODO ai: Understand which events to use
  menusEditorStyleAiGenerateOptionsSrc159Evid373,
  menusEditorStyleAiOptionLoadedSrc159Evid374,
  menusEditorStyleAiActionSrc159Evid376,
  menusMultipleImageActionSrc159Evid150,
} from '@wix/bi-logger-restaurants-data/v2';
import type {
  menusEditorStyleAiGenerateOptionsSrc159Evid373Params,
  menusEditorStyleAiOptionLoadedSrc159Evid374Params,
  menusEditorStyleAiActionSrc159Evid376Params,
} from '@wix/bi-logger-restaurants-data/v2/types';
import { useBi, useEditorSdk } from '@wix/yoshi-flow-editor';
import { useEffect, useRef } from 'react';
import MenuWidget from 'root/components/Menus/.component.json';
import type { DisplaySettingsParamsValue } from 'root/components/Menus/Settings/Tabs/DisplayTab/DisplaySettingsParams';
import type { DisplayStylesParamsValues } from 'root/components/Menus/Settings/Tabs/DisplayTab/DisplayStylesParams';
import type { LayoutStylesParamsValues } from 'root/components/Menus/Settings/Tabs/LayoutTab/LayoutStylesParams';
import type { TabActions, TabIds } from 'root/components/Menus/Settings/consts';
import type { MenusDisplay, ImageAction } from 'root/consts';
import { MENU_WIDGET_NAME } from 'root/consts';
import { getDisplayStylesParamForBi, getLayoutStyleParamsForBi, getSiteData, getWidgetInstanceId } from 'root/utils/bi';

export type ReportMenusTabParams = {
  menusDisplay: MenusDisplay;
  menusIds: string[];
  numberOfVisibleMenus: number;
};

export const useMenusBiLogger = () => {
  const biLogger = useBi();
  const { editorSDK } = useEditorSdk();
  const widgetName = MENU_WIDGET_NAME;
  const widgetId = MenuWidget.id;
  const widgetInstanceId = useRef<string | undefined>('');
  const pageId = useRef<string | undefined>('');
  const sitePageName = useRef<string | undefined>('');

  useEffect(() => {
    const initializeParams = async () => {
      widgetInstanceId.current = await getWidgetInstanceId(editorSDK);
      const siteData = await getSiteData(editorSDK);
      pageId.current = siteData.pageId;
      sitePageName.current = siteData.sitePageName;
    };

    initializeParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportSettingsTabClicked = ({ currentTab, targetTab }: { currentTab: TabIds; targetTab: TabIds }) => {
    biLogger.report(
      menusEditorSettingsPanelTabSwitchSrc159Evid167({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        currentTab,
        targetTab,
      })
    );
  };

  const reportStyleAIOnGenerateClick = ({
    styleAiFlowId,
    promptIds,
    userDescription,
    keepSiteTheme,
    inputsForFirstPrompt,
    inputsForSecondPrompt,
    inputsForThirdPrompt,
    inputsForForthPrompt,
    inputsForFifthPrompt,
    menusIds,
  }: menusEditorStyleAiGenerateOptionsSrc159Evid373Params) => {
    biLogger.report(
      menusEditorStyleAiGenerateOptionsSrc159Evid373({
        pageId: pageId.current,
        widgetName,
        widgetId,
        sitePageName: sitePageName.current,
        styleAiFlowId,
        promptIds,
        userDescription,
        keepSiteTheme,
        inputsForFirstPrompt,
        inputsForSecondPrompt,
        inputsForThirdPrompt,
        inputsForForthPrompt,
        inputsForFifthPrompt,
        menusIds,
      })
    );
  };

  const reportStyleAIOnOptionLoaded = ({
    styleAiFlowId,
    promptId,
    promptOutputFirstPart,
    promptOutputSecondPart,
    promptOutputThirdPart,
    timeTaken,
    menuIds,
    error,
  }: menusEditorStyleAiOptionLoadedSrc159Evid374Params) => {
    biLogger.report(
      menusEditorStyleAiOptionLoadedSrc159Evid374({
        styleAiFlowId,
        promptId,
        promptOutputFirstPart,
        promptOutputSecondPart,
        promptOutputThirdPart,
        timeTaken,
        menuIds,
        error,
      })
    );
  };

  const reportStyleAIModalAction = ({
    styleAiFlowId,
    action,
    modalState,
    menusIds,
  }: menusEditorStyleAiActionSrc159Evid376Params) => {
    biLogger.report(
      menusEditorStyleAiActionSrc159Evid376({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        styleAiFlowId,
        action,
        modalState,
        menusIds,
      })
    );
  };

  const reportTabActionsClicked = ({ action, tabName }: { action: TabActions; tabName: TabIds }) => {
    biLogger.report(
      menusEditorSettingsPanelActionSrc159Evid166({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        widgetInstanceId: widgetInstanceId.current,
        action,
        tabName,
      })
    );
  };

  const reportSettingsPanelLayoutTabData = (layoutStyleParams: LayoutStylesParamsValues) => {
    const { layoutName, layoutParams } = getLayoutStyleParamsForBi(layoutStyleParams);

    biLogger.report(
      menusEditorSettingsPanelLayoutTabSrc159Evid168({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        widgetInstanceId: widgetInstanceId.current,
        layoutName,
        ...layoutParams,
      })
    );
  };

  const reportMenusTab = ({ menusDisplay, menusIds, numberOfVisibleMenus }: ReportMenusTabParams) => {
    biLogger.report(
      menusEditorSettingsPanelMenusTabSrc159Evid169({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        menusDisplay,
        menusIds: menusIds.join(', '),
        numberOfVisibleMenus: numberOfVisibleMenus.toString(),
      })
    );
  };

  const reportSettingsPanelDisplayTabData = (
    displayStyleParams: DisplayStylesParamsValues & Partial<DisplaySettingsParamsValue>
  ) => {
    const { isPlaceholderImage, isPricingOptions, displayValues } = getDisplayStylesParamForBi(displayStyleParams);
    biLogger.report(
      menusEditorSettingsPanelDisplayTabSrc159Evid172({
        widgetId,
        widgetName,
        pageId: pageId.current,
        sitePageName: sitePageName.current,
        displayValues,
        isPricingOptions,
        isPlaceholderImage,
      })
    );
  };

  const reportItemImageAction = ({ action }: { action: ImageAction }) => {
    biLogger.report(
      menusMultipleImageActionSrc159Evid150({
        action,
        origin: 'menu page',
        viewMode: 'live_site',
      })
    );
  };

  return {
    reportSettingsTabClicked,
    reportTabActionsClicked,
    reportSettingsPanelLayoutTabData,
    reportMenusTab,
    reportSettingsPanelDisplayTabData,
    reportStyleAIOnGenerateClick,
    reportStyleAIOnOptionLoaded,
    reportStyleAIModalAction,
    reportItemImageAction,
  };
};
